import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as authApi from '../services/authApi';
import FormField, { inputStyle } from '../components/FormField';
import Button from '../components/Button';

const STEP_EMAIL = 'email';
const STEP_OTP = 'otp';
const STEP_PASSWORD = 'password';
const STEP_DONE = 'done';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email);
      setInfo("If that email is registered, we've sent a 6-digit code to it. It expires in 10 minutes.");
      setStep(STEP_OTP);
    } catch (err) {
      setError(err.message || 'Could not send the code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.verifyResetOtp(email, otp);
      setResetToken(result.resetToken);
      setInfo(null);
      setStep(STEP_PASSWORD);
    } catch (err) {
      setError(err.message || 'Incorrect code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email);
      setInfo('A new code has been sent.');
    } catch (err) {
      setError(err.message || 'Could not resend the code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.resetPassword(resetToken, newPassword);
      setStep(STEP_DONE);
    } catch (err) {
      setError(err.message || 'Could not reset the password. Please start again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'var(--space-5)', background: 'var(--color-bg)',
    }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 4 }}>Reset your password</h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
            {step === STEP_EMAIL && "Enter your account's email address to receive a reset code."}
            {step === STEP_OTP && `Enter the 6-digit code sent to ${email}.`}
            {step === STEP_PASSWORD && 'Choose a new password for your account.'}
            {step === STEP_DONE && 'Your password has been reset.'}
          </p>
        </div>

        <div className="card" style={{ marginBottom: 0 }}>
          {error && (
            <div
              role="alert"
              style={{
                background: 'var(--color-accent-warm-soft)', border: '1px solid var(--color-danger)',
                color: 'var(--color-danger)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)',
                fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)',
              }}
            >
              {error}
            </div>
          )}
          {info && !error && (
            <div style={{
              background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)',
            }}
            >
              {info}
            </div>
          )}

          {step === STEP_EMAIL && (
            <form onSubmit={handleRequestOtp}>
              <FormField label="Email address">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </FormField>
              <Button type="submit" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Sending…' : 'Send reset code'}
              </Button>
            </form>
          )}

          {step === STEP_OTP && (
            <form onSubmit={handleVerifyOtp}>
              <FormField label="6-digit code">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ ...inputStyle, letterSpacing: '0.3em', fontSize: 'var(--font-size-lg)', textAlign: 'center' }}
                />
              </FormField>
              <Button type="submit" disabled={submitting || otp.length !== 6} style={{ width: '100%' }}>
                {submitting ? 'Verifying…' : 'Verify code'}
              </Button>
              <p style={{ textAlign: 'center', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-3)', marginBottom: 0 }}>
                Didn&apos;t get it?{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={submitting}
                  style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', padding: 0, font: 'inherit' }}
                >
                  Resend code
                </button>
              </p>
            </form>
          )}

          {step === STEP_PASSWORD && (
            <form onSubmit={handleSetNewPassword}>
              <FormField label="New password">
                <input
                  type="password"
                  required
                  autoFocus
                  minLength={10}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Confirm new password">
                <input
                  type="password"
                  required
                  minLength={10}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={inputStyle}
                />
              </FormField>
              <Button type="submit" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Saving…' : 'Set new password'}
              </Button>
            </form>
          )}

          {step === STEP_DONE && (
            <Button onClick={() => navigate('/login', { replace: true })} style={{ width: '100%' }}>
              Go to sign in
            </Button>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-5)' }}>
          <Link to="/login" style={{ color: 'var(--color-accent)' }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
